import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, ArrowLeft, CreditCard, Search, Download, 
  Calendar, User, MessageSquare, Loader2, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminChatCreditLogs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setUser(response.data);
      fetchLogs();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat-credit-logs`, {
        withCredentials: true
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load credit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isWithinDateRange = (dateString) => {
    if (dateFilter === 'all') return true;
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    switch (dateFilter) {
      case 'today':
        return diffDays < 1;
      case 'week':
        return diffDays < 7;
      case 'month':
        return diffDays < 30;
      default:
        return true;
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.employer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.employer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch && isWithinDateRange(log.created_at);
  });

  const totalCreditsAdded = filteredLogs.reduce((sum, log) => sum + log.credits_added, 0);

  const exportToCSV = () => {
    const headers = ['Date', 'Employer Name', 'Employer Email', 'Credits Added', 'Reason', 'Added By'];
    const rows = filteredLogs.map(log => [
      formatDate(log.created_at),
      log.employer_name || '',
      log.employer_email || '',
      log.credits_added,
      log.reason || '',
      log.added_by_name || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-credit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chat-credit-logs-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <CreditCard className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Chat Credit Logs</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchLogs}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              className="btn-primary text-sm flex items-center gap-2"
              disabled={filteredLogs.length === 0}
              data-testid="export-btn"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card bg-gradient-to-br from-orange-50 to-white" data-testid="total-logs-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <MessageSquare className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Log Entries</p>
                <p className="text-2xl font-bold text-slate-900">{filteredLogs.length}</p>
              </div>
            </div>
          </div>
          
          <div className="card bg-gradient-to-br from-green-50 to-white" data-testid="total-credits-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Credits Added</p>
                <p className="text-2xl font-bold text-slate-900">{totalCreditsAdded}</p>
              </div>
            </div>
          </div>
          
          <div className="card bg-gradient-to-br from-blue-50 to-white" data-testid="unique-employers-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Unique Employers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(filteredLogs.map(l => l.user_id)).size}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by employer name, email, or reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-400" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2"
                data-testid="date-filter"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="card overflow-hidden">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No credit logs found</p>
              <p className="text-sm text-slate-400 mt-1">
                {logs.length === 0 
                  ? "No chat credits have been manually added yet" 
                  : "Try adjusting your search or filter criteria"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="logs-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Employer
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Added By
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map((log) => (
                    <tr 
                      key={log.log_id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`log-row-${log.log_id}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">{formatDate(log.created_at)}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{log.employer_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{log.employer_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                          +{log.credits_added}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-slate-600 max-w-xs truncate" title={log.reason}>
                          {log.reason || 'No reason provided'}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 bg-slate-100 rounded-full flex items-center justify-center">
                            <Building2 className="h-3 w-3 text-slate-600" />
                          </div>
                          <span className="text-sm text-slate-600">{log.added_by_name || 'Admin'}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Footer */}
        {filteredLogs.length > 0 && (
          <div className="mt-4 text-sm text-slate-500 text-center">
            Showing {filteredLogs.length} of {logs.length} log entries
          </div>
        )}
      </div>
    </div>
  );
}
